import { makeStyles, MessageBar, MessageBarActions, MessageBarBody, MessageBarGroup, Text, tokens } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import * as React from 'react';
import { StakeholderCard } from '../components/StakeholderCard';
import { useNavigation } from '../hooks/routing';
import { AttachmentCard } from '../components/AttachmentCard';
import { useTimeSince } from '../hooks/useTimeSince';
import { Empty } from '../components/Empty';
import { useClient } from '../hooks/client';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/Button';
import { CMWhite } from '../components/icons';

const useStyles = makeStyles({
  wrapper: {
    overflowY: 'auto',
  },
  container: {
    paddingInline: tokens.spacingHorizontalXXL,
    paddingBlock: tokens.spacingVerticalXXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
  },
  heading: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightBold,
  },
  summaryText: {
    paddingInline: tokens.spacingHorizontalXXS,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalM,
    display: 'inline-block',
    marginLeft: tokens.spacingHorizontalM,
  },
  stakeholders: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  button: {
    textAlign: 'center',
  },
});

export const SummaryScreen = () => {
  const { params } = useNavigation<'summary'>();
  const styles = useStyles();
  const { configuration } = useClient();

  if (!configuration) return <></>;

  return (
    <div className={styles.wrapper}>
      <Banner timestamp={new Date(params.dateReceived)} />
      <div className={styles.container}>
        <div>
          <Text
            className={styles.heading}
            block
          >
            {configuration.entityDefinitions.content.fieldSchemaDetails.subject.name}
          </Text>
          <Text className={styles.summaryText}>{params.summary}</Text>
        </div>
        <Text
          className={styles.heading}
          block
        >
          {t('COMMON.ATTACHMENTS')}
        </Text>
        {params.attachments?.length > 0 ? (
          params.attachments.map((attachment) => (
            <AttachmentCard
              attachment={attachment}
              key={attachment.id}
            />
          ))
        ) : (
          <Empty
            text={t('SUMMARY.NO_ATTACHMENTS')}
            style={{ padding: 0 }}
          />
        )}
        <Text
          className={styles.heading}
          block
        >
          {configuration.entityDefinitions.stakeholder.pluralName}
        </Text>
        <div className={styles.stakeholders}>
          {params.stakeholders?.length > 0 ? (
            params.stakeholders.map((stakeholder) => (
              <StakeholderCard
                stakeholder={stakeholder}
                key={stakeholder.email}
              />
            ))
          ) : (
            <Empty
              text={t('SUMMARY.NO_STAKEHOLDERS', { stakeholderPluralName: configuration.entityDefinitions.stakeholder.pluralName })}
              style={{ padding: 0 }}
            />
          )}
        </div>
        <Button
          className={styles.button}
          size="large"
          icon={<CMWhite />}
          appearance="primary"
          as="a"
          target="_blank"
          rel="noreferrer"
          href={params.cmUrl}
        >
          {t('SUMMARY.VIEW_IN_CM')}
        </Button>
      </div>
    </div>
  );
};

const Banner = ({ timestamp }: { timestamp: Date }) => {
  const [isOpenInternal, setIsOpen] = React.useState<boolean>(true);

  const format = useTimeSince();
  const { t } = useTranslation();

  return (
    <MessageBarGroup>
      {isOpenInternal ? (
        <MessageBar
          intent="success"
          shape="square"
        >
          <MessageBarBody>{t('SUMMARY.MESSAGE_BAR', { timeSince: format(timestamp) })}</MessageBarBody>
          <MessageBarActions
            containerAction={
              <Button
                onClick={() => setIsOpen(false)}
                aria-label="dismiss"
                appearance="transparent"
                icon={<DismissRegular />}
              />
            }
          />
        </MessageBar>
      ) : null}
    </MessageBarGroup>
  );
};
